/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Checkbox, Popover} from "antd"
import {Icon} from "components/Icon"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton} from "components/buttons"
import styles from "./DailyAttendanceDetailsPopup.module.css"
import {attendanceService} from "services"
import {clone, cloneDeep, get, isEmpty} from "lodash"
import {convertTZ, handleError, toastSuccess, toastWarning} from "helpers"
import moment from "moment-timezone"
import {useCurrentProfilePermissions, useModel, useVisible} from "hooks"
import {AttendanceEvent, AttendanceEventType, AttendanceStatus, BreakEventType} from "types/attendance"
import cx from "classnames"
import {BaseTimePicker} from "components/DateTimePicker"
import {v4 as uuid} from "uuid"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {useMutation} from "@tanstack/react-query"
import {BaseLoading} from "components"
import {ConfirmPopup} from "uiKit"
import {Permissions} from "@edular/permissions"
import {YesNo} from "types/common"
import {getDailyAttendancePermissions} from "helpers/attendance"
import {Modules, useEdularModulesContext} from "@edular/modules"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type Props = {
    attendance: any
    timezone: any
    refetch: any
}

export function AttendanceForm(props: Props) {
    const {attendance, timezone, refetch} = props
    const deleteEventIds = useRef<number[]>([])
    const {t} = useTranslation(["studentServices"])
    const [listEvent, setListEvent] = useState([])
    const [isVerified, setIsVerified] = useState<boolean>()
    const [message, setMessage] = useState<string>("")
    const [showPopover, setShowPopover] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isExcused, setIsExcused] = useState(false)
    const [isMakeup, setIsMakeup] = useState(false)
    const [distEdEnabled, setDistEdEnabled] = useState(false)
    const [dateData, setDateData] = useState({
        checkInTime: "",
        checkOutTime: "",
        checkInDate: ""
    })
    const model = useModel()
    const timeFormat = model.getUserTimeFormat()
    const dateFormat = model.getUserDateFormat()
    const confirmDeletePopup = useVisible()
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()
    const [isDeleted, setIsDeleted] = useState(false)
    const currentProfilePermissions = useCurrentProfilePermissions()
    const statusCanEditAttd = get(attendance, ["student", "status", "allowsDailyAttendanceManagement"], true)
    const {canEdit, canAdd, canDelete} = getDailyAttendancePermissions({
        isLocked: attendance.isLocked,
        deletedAt: attendance.deletedAt,
        allowsDailyAttendanceManagement: statusCanEditAttd,
        currentProfilePermissions
    })
    const {isModuleEnable} = useEdularModulesContext()

    const deleteDailyAttendanceMutation = useMutation(attendanceService.deleteDailyAttendance, {
        onSuccess: () => {
            if (refetch) {
                refetch()
            }
            toastSuccess("Deleted successfully")
            setIsDeleted(true)
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const buildAttendanceDetail = useCallback(async () => {
        const events = (attendance.events ?? []).sort(function (left, right) {
            return moment.utc(left.createdAt).diff(moment.utc(right.createdAt))
        })
        const checkinEvent = events.find((item) => item.eventType === AttendanceEventType.Checkin)
        const checkoutEvent = events.find((item) => item.eventType === AttendanceEventType.Checkout)
        setDateData({
            ...dateData,
            checkInTime: checkinEvent
                ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(timeFormat)
                : "",
            checkOutTime: checkoutEvent
                ? moment(convertTZ(new Date(checkoutEvent.createdAt), timezone)).format(timeFormat)
                : "",
            checkInDate: checkinEvent
                ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(dateFormat)
                : ""
        })
        setListEvent(
            events.map((event) => {
                return {
                    ...event,
                    createdAt: moment(event.createdAt).tz(timezone)
                }
            })
        )
        setIsMakeup(attendance.isMakeup)
        setDistEdEnabled(attendance.distEdEnabled)
    }, [attendance, timezone])

    useEffect(() => {
        if (attendance) {
            buildAttendanceDetail()
            const status = attendance.status
            let isVerified = attendance.isVerified === YesNo.Yes
            if (status === AttendanceStatus.Approved && isVerified) {
                isVerified = true
            } else if (status === AttendanceStatus.Rejected && isVerified) {
                isVerified = false
            } else {
                isVerified = null
            }
            setMessage(attendance?.message ?? "")
            setIsVerified(isVerified)
            setIsExcused(isExcused)
        }
    }, [attendance])

    const loading = isSubmitting || deleteDailyAttendanceMutation.isLoading

    const renderTitle = (eventType: string) => {
        if (eventType === "check-out") {
            return t("popup.checkout")
        } else if (eventType === "check-in") {
            return t("popup.checkin")
        } else if (eventType === "break-start") {
            return "Out"
        } else if (eventType === "break-end") {
            return "In"
        } else if (eventType === "lessonStart") {
            return "Lesson Start"
        } else if (eventType === "lessonEnd") {
            return "Lesson End"
        } else if (eventType === "attendance-check") {
            return "In"
        } else {
            return "Select Type"
        }
    }

    const renderIconAttendance = (eventType: string, status: string) => {
        if (eventType === "check-out") {
            return (
                <Icon
                    icon="CHECKOUT_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.checkOutIcon}
                />
            )
        } else if (eventType === "check-in") {
            return (
                <Icon
                    icon="CHECKIN_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else if (eventType === "attendance-check") {
            return (
                <Icon
                    icon="ATTENDANCE_IN"
                    color={status === "success" ? "#18A957" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else {
            return <Icon icon="ARROW_DOWN" color={"grey"} className={styles.arrowIcon} />
        }
    }

    const onPressDeleteEvent = (event: AttendanceEvent) => {
        typeof event.id === "number" && deleteEventIds.current.push(event.id)
        const events = listEvent.filter((item) => item.id !== event.id)
        setListEvent([...events])
    }

    const onAddEvent = (e) => {
        let newListEvent = clone(listEvent)
        newListEvent.push({
            status: "success",
            id: uuid()
        })
        setListEvent(newListEvent)
    }

    const renderPopoverSelectEvent = (event: AttendanceEvent) => {
        const checkIn = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkin && item.status === "success" && !item.deletedAt
        )
        const checkOut = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkout && item.status === "success" && !item.deletedAt
        )
        let options = cloneDeep(EVENT_OPTIONS)
        if (event?.eventType) {
            options = options.filter((item) => item.value !== event.eventType)
        }
        if (checkIn) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkin)
        }
        if (checkOut) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkout)
        }
        if (!isBreakEnabled) {
            options = options.filter(
                (item) => ![AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.value)
            )
        }

        if (isEmpty(options)) {
            return null
        }

        return (
            <div className={styles.selectContainer}>
                {options.map((option) => (
                    <div
                        onClick={() => {
                            event.eventType = option.value
                            setListEvent([...listEvent])
                            showPopover[event.id] = false
                            setShowPopover({...showPopover})
                        }}
                        className={cx(styles.popupContainer, styles.hoverBackgroundGrey)}>
                        {renderIconAttendance(option.value, "success")}
                        <span style={{width: 120}} className={styles.infoLabel}>
                            {renderTitle(option.value)}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    const renderCheckInItem = (data: any) => {
        const event = listEvent.find((item) => item.id === data.id)
        const isDeleted = !!event.deletedAt
        const isRenderDelete = event?.status === "success" && canEdit && !isDeleted
        const geoLocation = event?.jsonData?.geoLocation

        return (
            <div
                key={event?.id ?? uuid()}
                style={{
                    opacity: isDeleted ? 0.5 : 1,
                    pointerEvents: isDeleted ? "none" : "auto"
                }}>
                <div className={styles.infoWrapper}>
                    {event?.status === "success" ? (
                        <>
                            <BaseTimePicker
                                style={{width: 130, marginRight: 24}}
                                value={data?.createdAt ? moment(convertTZ(new Date(data?.createdAt), timezone)) : ""}
                                format={timeFormat}
                                disabled={!!event?.id && !canEdit}
                                allowClear={false}
                                onChange={(date) => {
                                    event.createdAt = moment(date).tz(timezone, true)
                                    setListEvent([...listEvent])
                                }}
                                placeholder="Time"
                            />
                        </>
                    ) : (
                        <div style={{backgroundColor: "transparent"}} className={styles.hourLabel} />
                    )}
                    {event?.status === "success" ? (
                        <>
                            <Popover
                                placement="right"
                                color="white"
                                visible={showPopover[event.id]}
                                arrowContent={null}
                                onVisibleChange={(visible) => {
                                    showPopover[event.id] = visible
                                    setShowPopover({...showPopover})
                                }}
                                content={() => renderPopoverSelectEvent(event)}
                                trigger={!!event?.id && !canEdit ? [] : "click"}>
                                <div className={styles.popupContainer}>
                                    {renderIconAttendance(data?.eventType, data.status)}
                                    <div className={styles.eventName}>
                                        <span
                                            style={{width: 80, color: data?.eventType ? "black" : "rgba(0,0,0,0.7)"}}
                                            className={styles.infoLabel}>
                                            {renderTitle(data?.eventType)}
                                        </span>
                                        {typeof data?.id === "number" ? (
                                            <>
                                                <div className={styles.eventInfo}>
                                                    <span className={styles.eventId}>ID: {data.id} </span>
                                                    {data.synced ? (
                                                        <div className={styles.syncedIcon}>
                                                            <Icon
                                                                icon="APPROVE"
                                                                size={12}
                                                                color={"var(--success-400-base)"}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {data.sisId?.length ? (
                                                    <div className={styles.eventInfo}>
                                                        <span className={styles.eventId}>ExID: {data.sisId} </span>
                                                    </div>
                                                ) : null}
                                                {data.syncedAt || data.synced_at ? (
                                                    <div className={styles.eventInfo}>
                                                        <span className={styles.eventId}>
                                                            Synced:{" "}
                                                            {moment(
                                                                convertTZ(
                                                                    new Date(data.syncedAt || data.synced_at),
                                                                    timezone
                                                                )
                                                            ).format(`${dateFormat} ${timeFormat}`)}
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </Popover>
                            {isModuleEnable(Modules.AcademicLocationTrackingAttendance) &&
                            isModuleEnable(Modules.AcademicUseGeoFencing) ? (
                                [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                                    event?.eventType
                                ) ? (
                                    <Checkbox
                                        checked={event?.breakEventType === BreakEventType.Lunch}
                                        disabled={!!event?.id && !canEdit}
                                        onChange={(e) => {
                                            event.breakEventType =
                                                event.breakEventType === BreakEventType.Lunch
                                                    ? BreakEventType.Break
                                                    : BreakEventType.Lunch

                                            setListEvent([...listEvent])
                                        }}>
                                        <span className={styles.infoLabel}>Is Lunch</span>
                                    </Checkbox>
                                ) : (
                                    <div style={{width: 96.8}} />
                                )
                            ) : null}
                        </>
                    ) : (
                        <div className={styles.eventName}>
                            <span style={{width: 80}} className={styles.infoLabel}>
                                {renderTitle(data?.eventType)}
                            </span>
                            {typeof data?.id === "number" ? (
                                <>
                                    <div className={styles.eventInfo}>
                                        <span className={styles.eventId}>ID: {data.id} </span>
                                        {data.synced ? (
                                            <div className={styles.syncedIcon}>
                                                <Icon icon="APPROVE" size={12} color={"var(--success-400-base)"} />
                                            </div>
                                        ) : null}
                                    </div>
                                    {data.sisId?.length ? (
                                        <div className={styles.eventInfo}>
                                            <span className={styles.eventId}>ExID: {data.sisId} </span>
                                        </div>
                                    ) : null}
                                    {data.syncedAt || data.synced_at ? (
                                        <div className={styles.eventInfo}>
                                            <span className={styles.eventId}>
                                                Synced:{" "}
                                                {moment(
                                                    convertTZ(new Date(data.syncedAt || data.synced_at), timezone)
                                                ).format(`${dateFormat} ${timeFormat}`)}
                                            </span>
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    )}

                    <div className={styles.applyRoundingContainer}>
                        <Checkbox
                            checked={event?.applyRounding}
                            disabled={!!event?.id && !canEdit}
                            onChange={(e) => {
                                event.applyRounding = !event.applyRounding
                                setListEvent([...listEvent])
                            }}>
                            <span className={styles.infoLabel}>Apply Rounding</span>
                        </Checkbox>
                        {data?.createdByUserName?.length ? (
                            <span className={styles.eventId}>Created By: {data.createdByUserName} </span>
                        ) : null}
                        {data?.modifiedByUserName?.length ? (
                            <span className={styles.eventId}>Modified By: {data.modifiedByUserName} </span>
                        ) : null}
                        {isDeleted ? (
                            <span className={styles.eventId}>
                                Deleted At: {moment(data.deletedAt).format(model.getUserDateTimeFormat())}{" "}
                            </span>
                        ) : null}
                    </div>
                    {event?.status === "success" ? (
                        <BaseInput
                            value={event?.message ?? ""}
                            disabled={!!event?.id && !canEdit}
                            onChange={(text) => {
                                event.message = text
                                setListEvent([...listEvent])
                            }}
                            placeholder={t("popup.notes")}
                            className={styles.inputWidth}
                        />
                    ) : (
                        <div className={styles.inputWidth} />
                    )}
                    {isRenderDelete ? (
                        <div onClick={() => onPressDeleteEvent(event)}>
                            <Icon color={"var(--error-400-base)"} icon="DELETE" className={styles.deleteIcon} />
                        </div>
                    ) : null}
                </div>
            </div>
        )
    }

    const onSave = useCallback(async () => {
        let listEventAdd = listEvent.filter((item) => typeof item.id !== "number")

        if (listEventAdd?.length && listEventAdd.find((event) => !event.eventType)) {
            return toastWarning("You need to select the event type: Check-in/Check-out or In/Out for all events")
        }

        if (listEventAdd?.length && listEventAdd.find((event) => !event.createdAt)) {
            return toastWarning("You need to select the time for all events")
        }

        setIsSubmitting(true)
        try {
            let status = attendance.status

            if (isVerified === true) {
                status = AttendanceStatus.Approved
            } else if (isVerified === false) {
                status = AttendanceStatus.Rejected
            }

            const params: any = {
                attendanceId: attendance.id,
                status,
                message,
                isVerified: [AttendanceStatus.Approved, AttendanceStatus.Rejected].includes(status) ? true : undefined,
                isMakeup,
                distEdEnabled
            }
            if (params.status === "approved") {
                params.isExcusedAbsence = false
            } else {
                params.isExcusedAbsence = !!isExcused
            }
            listEventAdd = listEventAdd.map((item) => {
                const createdAt = moment(item.createdAt).tz(timezone, true)
                const startDate = moment(attendance?.date).tz(timezone, true)
                createdAt.set({
                    month: startDate.month(),
                    date: startDate.date(),
                    year: startDate.year()
                })
                return {
                    message: item.message ?? "",
                    applyRounding: !!item.applyRounding,
                    eventType: item.eventType,
                    status: "success",
                    attendanceId: attendance.id,
                    createdAt: createdAt.utc().toISOString(),
                    breakEventType: [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                        item.eventType
                    )
                        ? item.breakEventType
                        : null
                }
            })

            const events = listEvent
                .filter((item) => typeof item.id === "number" && !item.deletedAt)
                .map((item) => {
                    const createdAt = moment(item.createdAt).tz(timezone, true)
                    const startDate = moment(attendance?.date).tz(timezone, true)
                    createdAt.set({
                        month: startDate.month(),
                        date: startDate.date(),
                        year: startDate.year()
                    })
                    return {
                        id: item.id,
                        applyRounding: !!item.applyRounding,
                        message: item.message ?? "",
                        eventType: item.eventType,
                        createdAt: createdAt.utc().toISOString(),
                        breakEventType: [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                            item.eventType
                        )
                            ? item.breakEventType
                            : null
                    }
                })
            if (events?.length) {
                params.events = events
            }
            if (deleteEventIds.current?.length) {
                params.deleteEventIds = deleteEventIds.current
            }
            if (listEventAdd.length) {
                params.addEvents = listEventAdd
            }
            await attendanceService.updateDailyAttendance(params)
            deleteEventIds.current = []
            if (refetch) {
                await refetch()
            }

            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [isVerified, attendance, isExcused, listEvent, timezone, isMakeup, refetch, distEdEnabled, message])

    const onConfirmDeleteAttendance = useCallback(() => {
        const attendanceId = attendance.id
        deleteDailyAttendanceMutation.mutate({
            attendanceIds: [attendanceId]
        })
        confirmDeletePopup.close()
    }, [attendance])

    const checkInEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkin && event.status === "success" && !event.deletedAt
    )
    const checkOutEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkout && event.status === "success" && !event.deletedAt
    )
    const otherEvents = listEvent.filter(
        (event) =>
            (event.eventType !== AttendanceEventType.Checkout && event.eventType !== AttendanceEventType.Checkin) ||
            !!event.deletedAt
    )

    if (isDeleted) {
        return null
    }

    return (
        <div className={styles.attendanceFormWrap}>
            {checkInEvent.map((item) =>
                renderCheckInItem({
                    status: "failure",
                    eventType: AttendanceEventType.Checkin,
                    ...item
                })
            )}
            {otherEvents.map(renderCheckInItem)}
            {checkOutEvent.map((item) =>
                renderCheckInItem({
                    status: "failure",
                    eventType: AttendanceEventType.Checkout,
                    ...item
                })
            )}

            <div className={`${styles.flexContainer} ${styles.alignItems}`}>
                <BaseInput value={message} onChange={setMessage} disabled={!canEdit} placeholder={t("popup.notes")} />
            </div>
            <hr className={styles.attendanceDivider} />

            <div
                style={{
                    cursor: !canEdit ? "not-allowed" : "pointer",
                    pointerEvents: !canEdit ? "none" : "auto",
                    opacity: !canEdit ? 0.7 : 1
                }}
                className={styles.optionsWrap}>
                <div className={styles.approveAttendWrap}>
                    <div className={styles.attendedBodyItem} onClick={() => setIsVerified(true)}>
                        <span className={styles.checkLabel}>Approve</span>
                        <div className={styles.unchecked}>
                            <Icon
                                icon="APPROVE"
                                size={24}
                                color={isVerified === true ? "var(--success-400-base)" : "grey"}
                            />
                        </div>
                    </div>

                    <div className={styles.attendedBodyItem} onClick={() => setIsVerified(false)}>
                        <span className={styles.checkLabel}>Reject</span>

                        <div className={styles.unchecked}>
                            <Icon
                                icon="CROSS_CIRCLE"
                                size={24}
                                color={isVerified === false ? "var(--error-400-base)" : "grey"}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.approveAttendWrap} style={{flexDirection: "column", alignItems: "flex-start"}}>
                    {attendance.verifiedAt || attendance.verified_at ? (
                        <span className={styles.eventId}>
                            Verified At:{" "}
                            {moment(
                                convertTZ(new Date(attendance.verifiedAt || attendance.verified_at), timezone)
                            ).format(`${dateFormat} ${timeFormat}`)}
                        </span>
                    ) : null}
                    {attendance.approvedAt || attendance.approved_at ? (
                        <span className={styles.eventId}>
                            Approved At:{" "}
                            {moment(
                                convertTZ(new Date(attendance.approvedAt || attendance.approved_at), timezone)
                            ).format(`${dateFormat} ${timeFormat}`)}
                        </span>
                    ) : null}
                </div>
                <div className={styles.makeupDistEdWrap}>
                    <div className={styles.approveAttendWrap} onClick={() => setDistEdEnabled(!distEdEnabled)}>
                        <span className={styles.checkLabel}>DistEd</span>
                        <div
                            className={cx(styles.uncheckedMakeup, {
                                [styles.checked]: distEdEnabled
                            })}>
                            <Icon icon="TICK" className={styles.attendanceTickIcon} color="#e5e5e5" />
                        </div>
                    </div>
                    <div className={styles.approveAttendWrap} onClick={() => setIsMakeup(!isMakeup)}>
                        <span className={styles.checkLabel}>Makeup</span>
                        <div
                            className={cx(styles.uncheckedMakeup, {
                                [styles.checked]: isMakeup
                            })}>
                            <Icon icon="TICK" className={styles.attendanceTickIcon} color="#e5e5e5" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.actionContainer}>
                {model.isStaffOrAdmin() ? (
                    <>
                        <BaseButton disabled={!canAdd} title="Add Event" onClick={onAddEvent} />
                        <div className={styles.saveContainer}>
                            <SecondaryButton
                                disabled={!canDelete}
                                loading={loading}
                                onClick={confirmDeletePopup.open}
                                title={"Delete"}
                            />
                            <BaseButton
                                loading={loading}
                                disabled={!canEdit && !canAdd}
                                onClick={onSave}
                                title={t("popup.save")}
                            />
                        </div>
                    </>
                ) : null}
            </div>
            <BaseLoading isShow={loading} />
            <ConfirmPopup
                isVisible={confirmDeletePopup.isVisible}
                title={"Are you sure you want to delete this attendance?"}
                onClose={confirmDeletePopup.close}
                onConfirm={onConfirmDeleteAttendance}
            />
        </div>
    )
}
